import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

interface ISelectionSet {
  added: string[];
  removed: string[];
}

// Context for managing property selections across pagination/re-renders
interface IPropertySelectionContext {
  selectedPropertyIds: Set<string>;
  initialPropertyIds: Set<string>;
  toggleSelection: (propertyId: string) => void;
  setInitialSelections: (propertyIds: string[]) => void;
  setSelections: (selectionSet: ISelectionSet) => void;
  clearSelections: () => void;
  resetToInitial: () => void;
  hasChanges: boolean;
  getSelectedIds: () => string[];
}

const PropertySelectionContext = createContext<IPropertySelectionContext | null>(null);

export const usePropertySelection = () => {
  const context = useContext(PropertySelectionContext);
  if (!context) {
    throw new Error('usePropertySelection must be used within PropertySelectionProvider');
  }
  return context;
};

interface IPropertySelectionProviderProps {
  children: ReactNode;
}

const PropertySelectionProvider = ({ children }: IPropertySelectionProviderProps) => {
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<Set<string>>(new Set());
  const [initialPropertyIds, setInitialPropertyIds] = useState<Set<string>>(new Set());

  const toggleSelection = (propertyId: string) => {
    setSelectedPropertyIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      return newSet;
    });
  };

  const setInitialSelections = (propertyIds: string[]) => {
    const idsSet = new Set(propertyIds);
    setInitialPropertyIds(idsSet);
    setSelectedPropertyIds(new Set(idsSet)); // Start with initial selections
  };

  const setSelections = (selectionSet: ISelectionSet) => {
    const { added, removed } = selectionSet;
    setSelectedPropertyIds((prev) => {
      const newSet = new Set(prev);
      added.forEach((id) => newSet.add(id));
      removed.forEach((id) => newSet.delete(id));
      return newSet;
    });
  };

  const clearSelections = () => {
    setSelectedPropertyIds(new Set());
    setInitialPropertyIds(new Set());
  };

  const resetToInitial = () => {
    setSelectedPropertyIds(new Set(initialPropertyIds));
  };

  const hasChanges = useMemo(() => {
    if (selectedPropertyIds.size !== initialPropertyIds.size) return true;
    for (const id of selectedPropertyIds) {
      if (!initialPropertyIds.has(id)) return true;
    }
    return false;
  }, [selectedPropertyIds, initialPropertyIds]);

  const getSelectedIds = () => Array.from(selectedPropertyIds);

  const value: IPropertySelectionContext = {
    selectedPropertyIds,
    initialPropertyIds,
    toggleSelection,
    setInitialSelections,
    setSelections,
    clearSelections,
    resetToInitial,
    hasChanges,
    getSelectedIds
  };

  return (
    <PropertySelectionContext.Provider value={value}>{children}</PropertySelectionContext.Provider>
  );
};

export default PropertySelectionProvider;
